import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import type {FormEvent} from 'inferno';

import {CustomComponent, InputButton, Link} from '../components';
import {ProgressCircleIndeterminate} from '../components/ProgressCircle';
import {CheckBoxInput} from '../components/CheckBoxInput';
import {GoogleSigninButton} from '../components/GoogleSigninButton';
import {Icon} from '../components/Icon';
import dialog from '../dialog';
import {FormState, Form, MessagesGroup} from '../form';
import {downloadObjectUrl} from '../util';
import emitter from '../util/emitter';
import {isValidFileType, getMimeType} from '../util/files';
import getFilesArrayFromEvent from '../util/getFilesArrayFromEvent';
import {iOS} from '../util/isMobile';
import type {ResponseAndBodyError} from '../services/apiService';

export class AuthDialog extends CustomComponent {
    allowedAvatarFileTypes: string[];
    signupForms: string[];
    loginForms: string[];
    otherForms: string[];
    animationSrc: string;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);

        const gettextSub = this.context.gtService.gettextSub;

        this.allowedAvatarFileTypes = ['jpeg', 'png'];
        this.signupForms = ['googleSignupForm'];
        this.loginForms = ['loginForm', 'loginAssociatedAccountForm'];
        this.otherForms = ['signupSplashForm', 'scope-refresh'];
        const forms = [
            ...this.signupForms,
            ...this.loginForms,
            ...this.otherForms,
        ];

        if (props.prevState) {
            this.state = {
                ...this.state,
                ...props.prevState,
            };
        } else {
            let mode = this.props.mode;
            if (!mode) {
                mode = props.signupAllowed ? 'signup-splash' : 'login';
            }
            const newState = {
                mode, // ('signup-splash'|'signup-username-selection'|'login')
                username: '',
                email: '',
                password: '',
                tos: false,
                isDeveloperPage: props.page === 'developer',
                avatar: {
                    src: null,
                    file: null,
                    width: null,
                    height: null,
                },
            };
            forms.forEach((formName) => {
                newState[formName] = new FormState();
            });
            this.state = {
                ...this.state,
                ...newState,
            };
        }

        const animations = [
            '/assets/img/auth/animations/molang-abrazo.mp4',
            '/assets/img/auth/animations/invaderzim-dancing.mp4',
            '/assets/img/auth/animations/spongebob-dancing.mp4',
            '/assets/img/auth/animations/humpday-excited.mp4',
        ];
        this.animationSrc = animations[
            Math.floor(Math.random() * animations.length)];

        if (props.expiredSession) {
            this.state['loginForm'].addAPIErrors({
                error: {'': gettextSub('Your session has expired. Please log in.')},
            });
        }
    }

    componentDidMount() {
        emitter.on('DialogContainer.startClose', this.onDialogClose);
        this.registerAuthEvent('dialog-mounted');
    }

    componentWillUnmount() {
        emitter.off('DialogContainer.startClose', this.onDialogClose);
        this.registerAuthEvent('dialog-unmounted');
    }
    componentDidUpdate() {
    }
    @autobind
    onDialogClose(event) {
        if (this.state.shouldPromptOnDialogClose) {
            const gettextSub = this.context.gtService.gettextSub;
            const state = this.state;

            setTimeout(() => {
                dialog.open('confirmation-dialog', {
                    header: gettextSub('Are you sure you want to cancel?'),
                    message: gettextSub('Changes you made will not be saved.'),
                    confirmButtonText: gettextSub('Yes'),
                    denyButtonText: gettextSub('No'),
                    denyCallback: () => dialog.open('auth-dialog', {prevState: state}),
                });
            }, 0); // NOTE async so that confirmation dialog opens after auth dialog closes, otherwise confirmation dialog will close right away
        }
    }
    @autobind
    handleTenorV2AuthResponse(formName) {
        return (ret: {body: api.UserSingleBody}) => {
            const delocalizeUrlPath = this.context.gtService.delocalizeUrlPath;
            this.debug('handleTenorV2AuthResponse', 'cb', ret);
            const {body} = ret;
            const isSignup = this.signupForms.includes(formName);
            const delocalizedPath = delocalizeUrlPath(
                this.context.router.route.location.pathname);
            const blockRedirect = ['/gif-maker'].some((path) => {
                return delocalizedPath.startsWith(path);
            }) || formName === 'scope-refresh';
            this.debug('handleTenorV2AuthResponse', 'blockRedirect', blockRedirect, formName);
            const avatarUploadPending = formName === 'googleSignupForm' && this.state.avatar.file;
            let avatarUploaded = false;

            if (avatarUploadPending) {
                this.uploadAvatarImage(this.state.avatar.file);
                avatarUploaded = true;
            }

            const {authService} = this.context;

            const authData = {
                GoogleUser: this.state.GoogleUser,
                ...body,
            };
            this.closeDialogIfOpen();
            this.registerAuthEvent('login_completed', {
                'category': 'google',
                'actions': isSignup ? 'signup' : 'signin',
                'info': avatarUploaded ? 'avatar' : '',
            }).then(() => {
                if (authData.user) {
                    authService.setAuthData(authData, isSignup);
                }

                if (!blockRedirect && this.props.getLoggedInURL) {
                    const url = this.props.getLoggedInURL(body);
                    url && this.redirect(url);
                }
                if (this.props.loggedinCallback) {
                    window.setTimeout(this.props.loggedinCallback, 300);
                }
            });
        };
    }

    registerAuthEvent(eventName, params = {}) {
        switch (eventName) {
        case 'dialog-mounted': {
            eventName = 'signin_began';
            break;
        }
        case 'dialog-unmounted': {
            eventName = 'signin_closed';
            break;
        }
        }

        return this.context.apiService.registerEvent(eventName, params);
    }

    closeDialogIfOpen() {
        this.setState({shouldPromptOnDialogClose: false});
        this.props.dialog?.close();
    }

    @autobind
    handleTenorV2AuthError(formName) {
        const gettextSub = this.context.gtService.gettextSub;

        return ({body}) => {
            this.state[formName].addAPIErrors(body);
            this.triggerUpdate();
        };
    }

    @autobind
    handleGoogleAuthError(formName) {
        return (error, message) => {
            console.error(error);
            this.state[formName].addAPIErrors({error: {'': message}});
            this.triggerUpdate();
        };
    }

    @autobind
    signUpWithGoogle(event) {
        event.preventDefault();
        const formName = 'googleSignupForm';
        if (this.state[formName].valid()) {
            this.state[formName].submitted = true;
            const {GoogleUser, tos} = this.state;

            const googleAuthResponse = GoogleUser.getAuthResponse(true);
            const gaiaToken = googleAuthResponse.id_token;
            const accessToken = googleAuthResponse.access_token;
            const data = {
                gaiaToken,
                accessToken,
                username: this.state.username.trim(),
                tos,
                apitos: tos && this.state.isDeveloperPage,
            };
            this.context.apiService.googleSignupApiCall(data)
                .then(
                    this.handleTenorV2AuthResponse(formName),
                    this.handleTenorV2AuthError(formName))
                .then(this.triggerUpdate)
                .catch((error) => console.error('signup error: ', error));
        }
    }

    @autobind
    handleGoogleAuthSuccess(formName) {
        const gettextSub = this.context.gtService.gettextSub;

        const cb = ({GoogleUser}) => {
            this.debug('handleGoogleAuthSuccess', {GoogleUser});
            const {authService, googleAuthService} = this.context;
            if (GoogleUser.hasGrantedScopes(googleAuthService.TENOR_SCOPE)) {
                authService.tmpGoogleUser = GoogleUser;
                this.setStateSynchronously({GoogleUser});

                if (formName === 'scope-refresh') {
                    this.getGoogleTokenWhenAuthed(formName);
                } else {
                    this.loginWithGoogle(formName);
                }
            } else {
                googleAuthService.signinDialogWindow().then(({GoogleUser}) => {
                    cb({GoogleUser});
                }, (err) => {
                    dialog.open('popup-dialog', {
                        children: (
                            <div>
                                <h1>{gettextSub('Tenor was not granted the necessary permissions.')}</h1>
                            </div>
                        ),
                    });
                });
            }
        };
        return cb;
    }

    loginWithGoogle(formName) {
        const googleAuthResponse = this.state.GoogleUser.getAuthResponse(true);
        const gaiaToken = googleAuthResponse.id_token;
        const accessToken = googleAuthResponse.access_token;
        const data = {
            gaiaToken,
            accessToken,
            username: this.state.username.trim(),
        };
        this.context.apiService.googleLoginApiCall(data)
            .then(
                this.handleTenorV2AuthResponse(formName),
                this.handleLoginWithGoogleError(formName),
            )
            .then(this.triggerUpdate)
            .catch((error) => console.error('Google login error:', error));
    }

    @autobind
    handleLoginWithGoogleError(formName) {
        return (error: ResponseAndBodyError) => {
            this.debug('handleLoginWithGoogleError', error);
            // NOTE if logging into Tenor with Google token fails,
            // navigate thru signup flow unless !signupAllowed (eg. admin page)
            if (this.props.signupAllowed) {
                this.registerAuthEvent('signup_username_prompt_shown');
                this.setState({
                    mode: 'signup-username-selection',
                    shouldPromptOnDialogClose: true,
                    username: '',
                });
            } else {
                this.state[formName].addAPIErrors(error);
                this.triggerUpdate();
            }
        };
    }

    getGoogleTokenWhenAuthed(formName) {
        const googleAuthResponse = this.state.GoogleUser.getAuthResponse(true);
        const gaiaToken = googleAuthResponse.id_token;
        const accessToken = googleAuthResponse.access_token;
        this.debug('getGoogleTokenWhenAuthed', {gaiaToken, accessToken}, formName);
        this.context.apiService.googleGetTokenForLegacyAuth(
            {gaiaToken, accessToken})
            .then(
                this.handleTenorV2AuthResponse(formName),
                this.handleGetGoogleTokenWhenAuthedError(formName),
            )
            .then(this.triggerUpdate)
            .catch((error) => console.error('Google login error:', error));
    }

    @autobind
    handleGetGoogleTokenWhenAuthedError(formName) {
        return (error) => {
            if (Array.isArray(error)) {
                error = error[0];
            }
            if (!error.error) {
                error = {error: {'': error.message}};
            }
            this.state[formName].addAPIErrors(error);
            this.triggerUpdate();
        };
    }

    @autobind
    handleOnInput(event) {
        const formName = event.target.form.name;
        const form = this.state[formName];
        this.setStateSynchronously({
            [event.target.name]: event.target.value,
        });

        form.validateInput(event.target);
        this.clearGeneralFormErrors(formName);
        this.triggerUpdate();
    }

    @autobind
    checkBoxHandler(event) {
        this.setStateSynchronously({
            [event.target.name]: event.target.checked,
        });

        const formName = event.target.form.name;
        const form = this.state[formName];
        form.validateInput(event.target);
        this.clearGeneralFormErrors(formName);
        this.triggerUpdate();
    }

    @autobind
    handleDownload() {
        this.setState({
            downloadInProgress: true,
            currentDownloadIdx: 1,
            downloadTS: Date.now(),
        });
        this.props.trackEvent({
            eventName: 'editing_download_tap',
            params: {
                'viewindex': this.props.uploadQueue.length,
            },
        });
        this.downloadFiles(this.props.uploadQueue);
    }

    @autobind
    downloadFiles(queue) {
        if (!queue.length) {
            this.setState({
                downloadInProgress: false,
                downloadTS: null,
            });
            return;
        }
        const uploadObject = queue[0];

        const downloadGif = (objectUrl) => {
            return new Promise<void>((resolve, reject) => {
                // NOTE: anchor element 'click' may not fire if there is not a
                // delay since the last 'click'
                const minDelay = 300;
                const initiateDownload = () => {
                    downloadObjectUrl(objectUrl, 'tenor.gif');
                    this.setStateSynchronously({
                        downloadTS: Date.now(),
                        currentDownloadIdx: this.state.currentDownloadIdx + 1,
                    });
                    this.triggerUpdate();
                    resolve();
                };
                const timeSinceLastDownload = this.state.downloadTS ?
                    this.state.downloadTS - Date.now() : 0;
                if (timeSinceLastDownload < minDelay) {
                    setTimeout(() => {
                        initiateDownload();
                    }, minDelay - timeSinceLastDownload);
                } else {
                    initiateDownload();
                }
            });
        };

        return uploadObject.getObjectUrl()
            .then(downloadGif)
            .then(() => this.downloadFiles(queue.slice(1)));
    }

    @autobind
    handleOnChange(event) {
        const newState = {};
        newState[event.target.name] = event.target.value;
        this.setState(newState);
        this.state[event.target.form.name].validateInput(event.target);
    }

    renderTOSLanguage() {
        const gettextSub = this.context.gtService.gettextSub;
        const gettextSubComponent = this.context.gtService.gettextSubComponent;
        const localizeUrlPath = this.context.gtService.localizeUrlPath;

        const tos = <Link external={true} to={localizeUrlPath('/legal-terms')}>{gettextSub('Terms of Service')}</Link>;
        const privacyPolicy = <Link external={true} to={localizeUrlPath('/legal-privacy')}>{gettextSub('Privacy Policy')}</Link>;
        const apiTos = <Link external={true} to='/gifapi/documentation#apiterms'>{gettextSub('API Terms of Service')}</Link>;

        let text;

        if (this.state.mode === 'signup-username-selection' || this.state.mode === 'login') {
            if (this.state.isDeveloperPage) {
                text = gettextSubComponent('I agree to Tenor\'s {tos}, {privacyPolicy}, and {apiTos}', {tos, privacyPolicy, apiTos});
            } else {
                text = gettextSubComponent('I agree to Tenor\'s {tos} and {privacyPolicy}', {tos, privacyPolicy});
            }
        } else if (this.state.mode === 'signup-splash') {
            if ( this.state.isDeveloperPage) {
                text = gettextSubComponent('By clicking Sign in with Google, you agree to our {tos}, {privacyPolicy}, and {apiTos}', {tos, privacyPolicy, apiTos});
            } else {
                text = gettextSubComponent('By clicking Sign in with Google, you agree to our {tos} and {privacyPolicy}', {tos, privacyPolicy});
            }
        }

        return <span className="tos-info"> {text} </span>;
    }

    renderTOSCheckboxSelector() {
        const gettextSub = this.context.gtService.gettextSub;
        return (
            <div className="signup-tos-checkbox">
                <CheckBoxInput
                    id="signup_tos"
                    name="tos"
                    checked={this.state.tos}
                    checkHandler={this.checkBoxHandler}
                    label={gettextSub('Agree to terms of service')}
                    boxSize={'24px'}
                    checkSize={'13px'}
                />
                { this.renderTOSLanguage() }
            </div>

        );
    }

    @autobind
    clearGeneralFormErrors(formName) {
        delete this.state[formName].errors[''];
        this.triggerUpdate();
    }

    renderSignUpSplash() {
        const gettextSub = this.context.gtService.gettextSub;
        const formName = 'signupSplashForm';
        const form = this.state[formName];

        return (
            <div className="content signup-splash">
                <div className="header mobile-only">
                    <img
                        src="/assets/img/tenor-logo-white.svg"
                        width="80"
                        height="22"
                        alt="Tenor"
                        itemProp="logo"
                    />
                </div>

                <div className="other-content non-mobile-only">
                    <h1>{gettextSub('Say more with the perfect GIF.')}</h1>
                    <img
                        src="/assets/img/tenor-logo-white.svg"
                        width="80"
                        height="22"
                        alt="Tenor"
                        itemProp="logo"
                    />
                </div>

                <Form className="auth-content signup-splash">
                    <div className="top">
                        <h1>{gettextSub('Create a Tenor Account')}</h1>
                        <p>{gettextSub('Search, share and upload your own GIFs to your Tenor account and access them anywhere, anytime.')}</p>

                        <GoogleSigninButton
                            onClick={
                                () => this.clearGeneralFormErrors(formName)}
                            successCallback={
                                this.handleGoogleAuthSuccess(formName)}
                            failureCallback={
                                this.handleGoogleAuthError(formName)}
                        />
                    </div>

                    <div className="bottom">
                        <div className="errors-section">
                            <MessagesGroup state={form}/>
                        </div>

                        <div className="signup-tos-info">
                            { this.renderTOSLanguage() }
                        </div>

                        {/* NOTE downloading gifs on iOS not working */}
                        { this.props.forceLogInToUpload && !iOS() &&
                            <div className="download-button white-button" onClick={this.handleDownload}>
                                { this.state.downloadInProgress ?
                                    <div style={{'display': 'flex', 'justify-content': 'center', 'align-items': 'center'}}>
                                        <ProgressCircleIndeterminate
                                            diameter={14}
                                            strokeWidthRatio={.15}
                                            color={'#007add'}
                                            animationDuration={1000}
                                        />
                                        <span style={{'margin-left': '5px'}}>
                                            {`${gettextSub('PREPARING YOUR GIFS')} (${this.state.currentDownloadIdx}/${this.props.uploadQueue.length})`}
                                        </span>
                                    </div> :
                                    <div>
                                        {
                                            // Translators: For a button that
                                            // Translators: lets users download
                                            // Translators: their Gifs.
                                            gettextSub('DOWNLOAD YOUR GIFS')
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </Form>
            </div>
        );
    }

    renderSignUpUsernameSelection() {
        const gettextSub = this.context.gtService.gettextSub;
        const formName = 'googleSignupForm';
        const form = this.state[formName];

        return (
            <div className="content">
                <div className="header">
                    <img
                        src="/assets/img/tenor-logo-white.svg"
                        width="80"
                        height="22"
                        alt="Tenor"
                        itemProp="logo"
                    />
                </div>

                <Form
                    className="auth-content signup-username"
                    name={formName}
                    onSubmit={this.signUpWithGoogle}
                >
                    <div className="top">
                        <h1>{gettextSub('Almost done!')}</h1>
                        <p>{gettextSub('Complete your profile')}</p>

                        { this.renderAvatarSelector() }

                        <div>
                            <label
                                className="hidden"
                                htmlFor="signup_username"
                            >{gettextSub('Username')}</label>
                            <div className={`username-input-wrapper`}>
                                <input
                                    id="signup_username"
                                    className={form.touchedInputErrors('username').length ? 'error' : ''}
                                    type="text"
                                    value={this.state.username}
                                    onInput={this.handleOnInput}
                                    onBlur={this.handleOnChange}
                                    name="username"
                                    placeholder={gettextSub('Choose your username')}
                                    autoComplete="off"
                                    required={true}
                                />
                                <div className={
                                    `${this.state.username.length ? 'focused' : ''}`}
                                >@</div>
                            </div>
                        </div>
                    </div>

                    <div className="bottom">
                        <div className="errors-section">
                            <MessagesGroup state={form}/>
                        </div>

                        { this.renderTOSCheckboxSelector() }

                        <InputButton
                            type="submit"
                            value={gettextSub('DONE')}
                            clickLabel="sign-up"
                            disabled={form.submitDisabled() ||
                                !this.state.username ||
                                !this.state.GoogleUser || !this.state.tos}
                        />
                    </div>
                </Form>
            </div>
        );
    }

    renderAvatarSelector() {
        const {src, width, height} = this.state.avatar;

        let className;
        if (src) {
            if (width < height) {
                className = 'portrait';
            } else if (width > height) {
                className = 'landscape';
            } else {
                className = 'square';
            }
        }

        return (
            <label
                htmlFor={`avatar-selector`}
                className={`avatar-selector`}
            >
                <input
                    id={`avatar-selector`}
                    type="file"
                    name="files"
                    onChange={this.updateAvatar}
                    accept={this.allowedAvatarFileTypes.map(getMimeType).join(', ')}
                />
                { !src &&
                    <div className="avatar-circle">
                        <Icon name="profile-icon-new"/>
                    </div>
                }
                { src &&
                    <div className="avatar-circle">
                        <img src={src} className={className}/>
                    </div>
                }
                <div className="plus-circle">
                    <Icon name='plus-circle'/>
                </div>
            </label>
        );
    }

    @autobind
    updateAvatar(event: FormEvent<HTMLInputElement>|DragEvent) {
        const gettextSub = this.context.gtService.gettextSub;
        const filesAry: File[] = getFilesArrayFromEvent(event);
        if (filesAry.length) {
            const file = filesAry[0];
            const formName = 'googleSignupForm';
            const form = this.state[formName];

            if (isValidFileType({file}, this.allowedAvatarFileTypes)) {
                const src = window.URL.createObjectURL(file);

                const img = new Image();
                img.addEventListener('load', () => {
                    this.setState({avatar: {
                        file,
                        src,
                        width: img.width,
                        height: img.height,
                    }});
                });
                img.addEventListener('error', () => {
                    console.error('Avatar image loading error');
                    form.addAPIErrors({
                        error: {'': gettextSub('Could not process avatar image.')},
                    });
                    this.setState({avatar: {
                        file: null,
                        src: null,
                    }});
                });
                img.src = src;
            } else {
                form.addAPIErrors({
                    error: {'': gettextSub('Avatar must be a PNG or JPEG')},
                });
                this.triggerUpdate();
            }
        }
    }

    uploadAvatarImage(file) {
        if (file) {
            return this.context.apiService.setAvatar(file)
                .catch((error) => console.error('avatar save error: ', error));
        } else {
            return;
        }
    }

    renderScopeRefresh() {
        const gettextSub = this.context.gtService.gettextSub;
        const formName = 'scope-refresh';
        const form = this.state[formName];
        let permissionText;
        if (this.props.permissionText) {
            permissionText = this.props.permissionText;
        } else {
            let accountIdentifier;
            if (this.context.authService.isLoggedIn()) {
                accountIdentifier =
                    this.context.authService.getGoogleUserEmail();
            }
            if (accountIdentifier) {
                permissionText = gettextSub('Please grant Tenor permission to access your Google account: {accountIdentifier}.', {
                    accountIdentifier,
                });
            } else {
                permissionText = gettextSub('Please grant Tenor permission to access your Google account.');
            }
        }
        return (
            <div className="content">
                <div className="header">
                    <img
                        src="/assets/img/tenor-logo-white.svg"
                        width="80"
                        height="22"
                        alt="Tenor"
                        itemProp="logo"
                    />
                </div>

                <Form className="auth-content account-linking">
                    <div className="top">
                        <h1>{gettextSub('Grant Tenor Permissions')}</h1>
                        <p>{permissionText}</p>
                    </div>

                    <div className="bottom">
                        <div className="errors-section">
                            <MessagesGroup state={form}/>
                        </div>

                        <GoogleSigninButton
                            onClick={
                                () => this.clearGeneralFormErrors(formName)}
                            successCallback={
                                this.handleGoogleAuthSuccess(formName)}
                            failureCallback={
                                this.handleGoogleAuthError(formName)}
                        />
                    </div>
                </Form>
            </div>
        );
    }

    renderLogIn() {
        this.debug('renderLogIn');
        const gettextSub = this.context.gtService.gettextSub;
        const formName = 'loginForm';

        return (
            <div className="content">
                <div className="header">
                    <img
                        src="/assets/img/tenor-logo-white.svg"
                        width="80"
                        height="22"
                        alt="Tenor"
                        itemProp="logo"
                    />
                </div>
                <div className="auth-content login">
                    <div className="top">
                        <h1>{gettextSub('Welcome back!')}</h1>

                        <div className="google-signin-section">
                            <GoogleSigninButton
                                onClick={
                                    () => this.clearGeneralFormErrors(formName)}
                                successCallback={
                                    this.handleGoogleAuthSuccess(formName)}
                                failureCallback={
                                    this.handleGoogleAuthError(formName)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const videoSrc = this.animationSrc;
        const mode = this.state.mode;

        return (
            <div id={this.props.id} className="auth-dialog">
                <video
                    className="background-animation"
                    muted={true}
                    playsInline={true}
                    autoPlay={true}
                    loop={true}
                    key={videoSrc}
                >
                    <source src={videoSrc} type="video/mp4" />
                </video>
                <div className="video-filter"/>

                { mode === 'signup-splash' && this.props.signupAllowed &&
                    this.renderSignUpSplash() }

                { mode === 'signup-username-selection' &&
                    this.props.signupAllowed &&
                    this.renderSignUpUsernameSelection() }

                { mode === 'login' && this.renderLogIn() }

                { mode === 'scope-refresh' && this.renderScopeRefresh() }

            </div>
        );
    }
}
